import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight, Users, Lightbulb, GraduationCap, Heart, CalendarDays, Building2, Award } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";

const CAT_META = {
  idea:        { label: "Idea Contributors",   blurb: "People who shaped Pop Panda with their thinking.",     Icon: Lightbulb,    accent: "#FFC93C" },
  ambassador:  { label: "Campus Ambassadors",  blurb: "Students who carry the Pop Panda flag on their campus.", Icon: GraduationCap, accent: "#FF4F5A" },
  early:       { label: "Early Supporters",    blurb: "The first hundred who said yes before there was a brand.", Icon: Heart,    accent: "#7DD3C0" },
  event:       { label: "Event Partners",      blurb: "Cafés, schools and venues where Pop Panda showed up.",   Icon: CalendarDays, accent: "#7B9EFF" },
  brand:       { label: "Brand Contributors",  blurb: "Designers, writers and storytellers behind the brand.",  Icon: Building2,    accent: "#C9A977" },
  special:     { label: "Special Recognition", blurb: "People who don't fit a box — and deserve their own one.", Icon: Award,       accent: "#F5A623" },
  supporter:   { label: "Supporters",          blurb: "Everyone else who quietly helped along the way.",        Icon: Users,        accent: "#2B2B2B" },
};
const ORDER = ["idea", "ambassador", "early", "event", "brand", "special", "supporter"];

export default function Contributors() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contributors").then((r) => setItems(r.data || [])); }, []);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((c) => {
      const k = c.category || "supporter";
      if (!g[k]) g[k] = [];
      g[k].push(c);
    });
    return g;
  }, [items]);

  const totalNames = items.length;
  const totalSections = Object.keys(grouped).length;

  return (
    <div data-testid="contrib-page" className="section-pad">
      <div className="container-pp">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Contributors Wall</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="editorial-h1 text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]">
              People who <em className="font-display italic">helped build</em> Pop Panda.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/70 leading-relaxed">
            <p>A working brand is built by many hands — not by a press release.</p>
            <p className="mt-3 text-brand-charcoal">Below: every contributor, ambassador, early supporter and event partner who shaped Pop Panda. Past, present and (we hope) future.</p>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-5 border-y border-brand-line py-8">
          <div><div className="font-display text-4xl md:text-5xl">{totalNames}</div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">People credited</div></div>
          <div><div className="font-display text-4xl md:text-5xl">{totalSections}</div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">Contribution types</div></div>
          <div><div className="font-display text-4xl md:text-5xl">0</div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">Names hidden</div></div>
          <div><div className="font-display text-4xl md:text-5xl">∞</div><div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-2">Spots still open</div></div>
        </div>

        {/* Groups */}
        <div className="mt-20 space-y-24" data-testid="contrib-groups">
          {ORDER.filter((k) => grouped[k]?.length).map((cat) => {
            const meta = CAT_META[cat] || { label: cat, Icon: Users, accent: "#2B2B2B", blurb: "" };
            const list = grouped[cat];
            return (
              <section key={cat} data-testid={`contrib-cat-${cat}`}>
                {/* Section hero */}
                <div className="grid lg:grid-cols-12 gap-8 items-end">
                  <div className="lg:col-span-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl"
                      style={{ background: `${meta.accent}25`, color: meta.accent === "#FFC93C" ? "#2B2B2B" : meta.accent }}>
                      <meta.Icon size={22} />
                    </div>
                    <div className="font-display text-4xl md:text-5xl mt-5 leading-none">{meta.label}</div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55 mt-3">{list.length} {list.length === 1 ? "person" : "people"}</div>
                  </div>
                  <div className="lg:col-span-8">
                    <p className="text-brand-charcoal/70 leading-relaxed">{meta.blurb}</p>
                    <div className="editorial-divider mt-5" />
                  </div>
                </div>

                {/* Grid */}
                <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {list.map((c, i) => (
                    <motion.div key={c.id}
                      initial={{ opacity: 0, y: 14 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}
                      className="card-elevated p-6 text-center hover:-translate-y-1 transition-transform duration-300"
                      data-testid={`contrib-${c.id}`}>
                      <div className="relative mx-auto w-24 h-24">
                        <div className="absolute inset-0 rounded-full" style={{ background: `${meta.accent}25` }} />
                        <div className="absolute inset-1 rounded-full overflow-hidden bg-brand-muted">
                          {c.image_url
                            ? <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" />
                            : <div className="h-full w-full grid place-items-center font-display text-3xl" style={{ color: meta.accent }}>{c.name.charAt(0)}</div>}
                        </div>
                      </div>
                      <div className="font-display text-xl mt-5">{c.name}</div>
                      {c.role && <div className="text-[10px] uppercase tracking-[0.22em] mt-1.5" style={{ color: meta.accent === "#FFC93C" ? "#2B2B2B" : meta.accent }}>{c.role}</div>}
                      {c.description && <p className="mt-3 text-xs text-brand-charcoal/70 leading-relaxed">{c.description}</p>}
                      {c.link_url && <a href={c.link_url} target="_blank" rel="noreferrer" className="text-xs mt-3 inline-flex items-center gap-1 hover:text-brand-red">Visit <ArrowUpRight size={11} /></a>}
                    </motion.div>
                  ))}
                </div>
              </section>
            );
          })}

          {ORDER.every((k) => !grouped[k]?.length) && (
            <div className="text-brand-charcoal/60">Contributors are being added — check back soon.</div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-24 relative overflow-hidden rounded-3xl bg-brand-charcoal text-brand-cream p-10 lg:p-16">
          <div className="absolute -right-32 -top-32 w-[28rem] h-[28rem] rounded-full bg-brand-yellow/15 blur-3xl" />
          <div className="absolute -left-40 -bottom-40 w-[24rem] h-[24rem] rounded-full bg-brand-red/15 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.3em] text-brand-yellow font-semibold flex items-center gap-2"><Sparkles size={14} /> Want to be on this wall?</div>
              <h3 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95] text-white">Your idea could be<br /><em className="italic">on this wall.</em></h3>
              <p className="mt-5 text-brand-cream text-base md:text-lg max-w-lg leading-relaxed">Submit a flavour. Solve a challenge. Become a campus ambassador. We credit every contribution — by name.</p>
            </div>
            <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
              <Link to="/challenges" className="btn-pill btn-primary text-sm" data-testid="contrib-cta-challenges">Solve a Challenge</Link>
              <Link to="/flavour-creator" className="btn-pill btn-outline-cream text-sm" data-testid="contrib-cta-flavour">Submit a Flavour</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
